import { useCallback, useRef, useState } from 'react';

export type OfflinePreparationStepStatus = 'pending' | 'loading' | 'success' | 'error';

export interface OfflinePreparationProgressDetail {
  label: string;
  status: OfflinePreparationStepStatus;
  count?: number;
}

export interface OfflinePreparationProgressState {
  step: string;
  current: number;
  total: number;
  details: OfflinePreparationProgressDetail[];
}

interface RunPreparationOptions {
  details: Array<Pick<OfflinePreparationProgressDetail, 'label'> & Partial<Pick<OfflinePreparationProgressDetail, 'count'>>>;
  openModal?: boolean;
  onRetryReset?: () => Promise<void> | void;
  task: (helpers: {
    updateProgress: (step: string, index: number, status: Exclude<OfflinePreparationStepStatus, 'pending'>, count?: number) => void;
    getProgress: () => OfflinePreparationProgressState | null;
  }) => Promise<void>;
}

export interface UseMasterSyncOfflinePreparationReturn {
  isOpen: boolean;
  status: 'idle' | 'preparing' | 'ready' | 'error';
  progress: OfflinePreparationProgressState | null;
  closeModal: () => void;
  setOpen: (nextOpen: boolean) => void;
  runPreparation: (options: RunPreparationOptions) => Promise<boolean>;
}

export function useMasterSyncOfflinePreparation(): UseMasterSyncOfflinePreparationReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'preparing' | 'ready' | 'error'>('idle');
  const [progress, setProgress] = useState<OfflinePreparationProgressState | null>(null);
  const progressRef = useRef<OfflinePreparationProgressState | null>(null);
  const isPreparingRef = useRef(false);

  const setProgressState = useCallback((nextProgress: OfflinePreparationProgressState | null) => {
    progressRef.current = nextProgress;
    setProgress(nextProgress);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const updateProgress = useCallback((step: string, index: number, nextStatus: Exclude<OfflinePreparationStepStatus, 'pending'>, count?: number) => {
    const currentProgress = progressRef.current;
    if (!currentProgress) {
      return;
    }

    const nextDetails = currentProgress.details.map((detail, detailIndex) => {
      if (detailIndex !== index) {
        return detail;
      }

      return {
        ...detail,
        status: nextStatus,
        count: count ?? detail.count,
      };
    });

    setProgressState({
      step,
      current: nextDetails.filter((detail) => detail.status === 'success').length,
      total: nextDetails.length,
      details: nextDetails,
    });
  }, [setProgressState]);

  const runPreparation = useCallback(async (options: RunPreparationOptions): Promise<boolean> => {
    if (isPreparingRef.current) {
      return false;
    }

    if (status === 'error' && options.onRetryReset) {
      await options.onRetryReset();
    }

    isPreparingRef.current = true;

    const initialDetails: OfflinePreparationProgressDetail[] = options.details.map((detail) => ({
      label: detail.label,
      status: 'pending',
      count: detail.count,
    }));

    setIsOpen(options.openModal ?? true);
    setStatus('preparing');
    setProgressState({
      step: 'Preparing offline data...',
      current: 0,
      total: initialDetails.length,
      details: initialDetails,
    });

    try {
      await options.task({
        updateProgress,
        getProgress: () => progressRef.current,
      });

      const finalProgress = progressRef.current;
      const hasErrors = finalProgress?.details.some((detail) => detail.status === 'error') ?? false;
      const hasSuccess = finalProgress?.details.some((detail) => detail.status === 'success') ?? false;
      const nextStatus = hasErrors || !hasSuccess ? 'error' : 'ready';
      setStatus(nextStatus);
      return nextStatus === 'ready';
    } catch {
      setStatus('error');
      return false;
    } finally {
      isPreparingRef.current = false;
    }
  }, [setProgressState, status, updateProgress]);

  return {
    isOpen,
    status,
    progress,
    closeModal,
    setOpen: setIsOpen,
    runPreparation,
  };
}

export default useMasterSyncOfflinePreparation;
